import { SyncArAp } from "@/redux/Booking/actions";
import { ImportSyncArAp } from "@/redux/ImportBooking/actions";
import { ACCOUNTING_DIVISION } from "@/utils/accountingBookingUtils";

/** Charge stream synced to booking (AR → invoice, AP → bill). */
export const SYNC_CHARGE_KIND = {
    AR: "ar",
    AP: "ap",
};

function isImportDivision(division) {
    return (
        division === ACCOUNTING_DIVISION.IMPORT ||
        division === "Import"
    );
}

function mapLineToArCharge(line, document, { isDelete, createdBy }) {
    return {
        id: Number(line?.source_charge_id) || 0,
        chargeCode: String(line?.charge_code_id || ""),
        description: String(line?.description || ""),
        currency: document?.currency_code || "USD",
        roe: String(line?.roe ?? "1"),
        basis: line?.Basis || line?.basis || "",
        container: line?.container || "",
        quantity: String(line?.quantity ?? 0),
        billToParty: document?.customer_name || "",
        billToPartyId: Number(document?.customer_id) || 0,
        billToPartyRef: String(
            document?.vendor_invoice_no ||
                document?.customer_invoice_no ||
                "",
        ),
        amount: String(line?.amount ?? 0),
        sellRate: Number(line?.unit_price) || 0,
        ppcc: line?.ppcc || "P",
        invoiceStatus: isDelete ? "" : document?.status || "",
        invoiceId: isDelete ? "" : document?.id || "",
        invoiceNo: isDelete ? "" : document?.invoice_no || "",
        createdBy: createdBy || "SYSTEM",
        isactive: line?.isactive !== false,
    };
}

function mapLineToApCharge(line, document, { isDelete, createdBy }) {
    return {
        id: Number(line?.source_charge_id) || 0,
        chargeCode: String(line?.charge_code_id || ""),
        description: String(line?.description || ""),
        currency: document?.currency_code || "USD",
        roe: String(line?.roe ?? "1"),
        basis: line?.Basis || line?.basis || "",
        container: line?.container || "",
        quantity: String(line?.quantity ?? 0),
        vendor: document?.vendor_name || "",
        vendorId: Number(document?.vendor_id) || 0,
        vendorRef: String(document?.vendor_invoice_no || ""),
        amount: String(line?.amount ?? 0),
        buyRate: Number(line?.unit_price) || 0,
        ppcc: line?.ppcc || "P",
        billStatus: isDelete ? "" : document?.status || "",
        billId: isDelete ? "" : document?.id || "",
        billNo: isDelete ? "" : document?.bill_no || "",
        createdBy: createdBy || "SYSTEM",
        isactive: line?.isactive !== false,
    };
}

/**
 * Build Export or Import sync payload (same charge shapes; collection names differ).
 */
export function buildArApSyncPayload({
    document,
    chargeKind = SYNC_CHARGE_KIND.AR,
    syncType = "create",
    isSync = false,
    createdBy = "SYSTEM",
    division = ACCOUNTING_DIVISION.EXPORT,
}) {
    if (!document || !Array.isArray(document.lines)) {
        const emptyBookingId = Number(document?.booking_id) || 0;
        if (isImportDivision(division)) {
            return {
                bookingId: emptyBookingId,
                isSync: Boolean(isSync),
                fclBolImpArCharges: [],
                fclBolImpApCharges: [],
            };
        }
        return {
            bookingId: emptyBookingId,
            arCharges: [],
            apCharges: [],
        };
    }

    const isDelete = syncType === "delete";
    const bookingId = Number(document.booking_id) || 0;
    const lines = document.lines;

    if (chargeKind === SYNC_CHARGE_KIND.AR) {
        const arCharges = lines.map((line) =>
            mapLineToArCharge(line, document, { isDelete, createdBy }),
        );

        if (isImportDivision(division)) {
            return {
                bookingId,
                isSync: Boolean(isSync),
                fclBolImpArCharges: arCharges,
                fclBolImpApCharges: [],
            };
        }

        const payload = { bookingId, arCharges };
        if (syncType === "create" || syncType === "update") {
            payload.isSync = Boolean(isSync);
        }
        return payload;
    }

    const apCharges = lines.map((line) =>
        mapLineToApCharge(line, document, { isDelete, createdBy }),
    );

    if (isImportDivision(division)) {
        return {
            bookingId,
            isSync: Boolean(isSync),
            fclBolImpArCharges: [],
            fclBolImpApCharges: apCharges,
        };
    }

    const payload = { bookingId, apCharges };
    if (syncType === "create" || syncType === "update") {
        payload.isSync = Boolean(isSync);
    }
    return payload;
}

/** Route to Export or Import booking sync API (Export implementation unchanged). */
export function executeArApSync(payload, division = ACCOUNTING_DIVISION.EXPORT) {
    if (isImportDivision(division)) {
        return ImportSyncArAp(payload);
    }
    return SyncArAp(payload);
}

export function isArApSyncSuccess(response) {
    if (!response) return false;
    const status = response?.statuscode ?? response?.statusCode;
    return status === 200 || status === 201;
}
